import { useMemo, useState } from 'react';
import {
  AlertTriangle, Check, ClipboardCheck, Factory, PackageCheck, Plus,
  RefreshCw, Save, Trash2, X,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import {
  useDeclarerProduction, useProductionJournaliere, useRejeterQcLots, useValiderQcEtPlacer,
} from '@/hooks/useProductionJournaliere';
import type {
  AlerteStockProduction, ConsommationUnitaireMp, ProductionJournaliereOf,
} from '@/api/operations';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

type LigneStock = { key: number; produitId: string; quantite: string; notes: string };

/**
 * Production journalière — écran du responsable production.
 *
 * Deux régimes, dans l'ordre réel de l'atelier :
 *   1. Production pour stock (courant) — on fabrique pour alimenter le parc,
 *      sans commande ni OF préalable.
 *   2. Production sur commande — rattachée à un OF ouvert, pour les commandes
 *      spécifiques.
 *
 * Dans les deux cas, les matières premières sont déduites automatiquement et
 * les lots partent en file contrôle qualité avant d'entrer au parc.
 */
export const ProductionJournalierePage = () => {
  const [date, setDate] = useState(todayISO());
  const [saisies, setSaisies] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [lignesStock, setLignesStock] = useState<LigneStock[]>([]);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [zoneParc, setZoneParc] = useState('');
  const [emplacement, setEmplacement] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [alertes, setAlertes] = useState<AlerteStockProduction[]>([]);
  const [succes, setSucces] = useState<string | null>(null);

  const { data, isLoading, refetch } = useProductionJournaliere(date);
  const declarer = useDeclarerProduction();
  const validerQc = useValiderQcEtPlacer();
  const rejeterQc = useRejeterQcLots();

  const produits = data?.produits ?? [];
  const ordres = data?.ordres ?? [];
  const fileQc = data?.fileQc ?? [];
  const declarations = data?.declarationsDuJour ?? [];

  const lignesAEnvoyer = useMemo(() => {
    const surCommande = Object.entries(saisies)
      .map(([ordreId, v]) => ({ ordreId, quantiteProduite: parseFloat(v || '0'), notes: notes[ordreId]?.trim() || undefined }))
      .filter((l) => l.quantiteProduite > 0);
    const pourStock = lignesStock
      .filter((l) => l.produitId && parseFloat(l.quantite || '0') > 0)
      .map((l) => ({ produitId: l.produitId, quantiteProduite: parseFloat(l.quantite), notes: l.notes.trim() || undefined }));
    return [...pourStock, ...surCommande];
  }, [saisies, notes, lignesStock]);

  const handleDeclarer = async () => {
    if (lignesAEnvoyer.length === 0) return;
    setActionError(null); setSucces(null); setAlertes([]);
    try {
      const res = await declarer.mutateAsync({ date, lignes: lignesAEnvoyer });
      setSaisies({}); setNotes({}); setLignesStock([]);
      setAlertes(res.alertesStock);
      setSucces(
        res.message +
          (res.ofClotures.length ? ` · OF clôturé(s) : ${res.ofClotures.join(', ')}` : '') +
          (res.ofStockCrees.length ? ` · OF de stock : ${res.ofStockCrees.join(', ')}` : ''),
      );
    } catch (e) { setActionError(extractError(e).message); }
  };

  const handleValiderQc = async () => {
    if (selection.size === 0 || !zoneParc.trim()) return;
    setActionError(null); setSucces(null);
    try {
      const res = await validerQc.mutateAsync({
        lotIds: [...selection],
        zoneParc: zoneParc.trim(),
        emplacementParc: emplacement.trim() || undefined,
      });
      setSelection(new Set()); setZoneParc(''); setEmplacement('');
      setSucces(res.message);
    } catch (e) { setActionError(extractError(e).message); }
  };

  const handleRejeter = async () => {
    if (selection.size === 0) return;
    const motif = await demander.texte({ titre: 'Motif du rejet qualité', message: 'Obligatoire, tracé pour la DNC.', min: 1, confirmer: 'Rejeter', danger: true });
    if (motif === null) return;
    setActionError(null); setSucces(null);
    try {
      const res = await rejeterQc.mutateAsync({ lotIds: [...selection], motif: motif.trim() });
      setSelection(new Set());
      setSucces(res.message);
    } catch (e) { setActionError(extractError(e).message); }
  };

  const toggle = (id: string) => setSelection((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelection((prev) => (prev.size === fileQc.length ? new Set() : new Set(fileQc.map((l) => l.id))));

  const ajouterLigneStock = () =>
    setLignesStock((l) => [...l, { key: Date.now() + l.length, produitId: '', quantite: '', notes: '' }]);
  const majLigneStock = (key: number, champ: keyof LigneStock, valeur: string) =>
    setLignesStock((l) => l.map((x) => (x.key === key ? { ...x, [champ]: valeur } : x)));
  const retirerLigneStock = (key: number) => setLignesStock((l) => l.filter((x) => x.key !== key));

  const busy = declarer.isPending || validerQc.isPending || rejeterQc.isPending;

  return (
    <>
      <Topbar
        title="Production journalière"
        subtitle="Production pour stock et sur commande — déstockage matières automatique, puis contrôle qualité avant parc"
        actions={
          <>
            <input
              type="date"
              className="ds-fi"
              value={date}
              max={todayISO()}
              onChange={(e) => { setDate(e.target.value); setSaisies({}); setNotes({}); setLignesStock([]); setAlertes([]); setSucces(null); }}
              style={{ width: 'auto' }}
            />
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} aria-label="Rafraîchir">
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button
              className="ds-btn ds-btn-p"
              disabled={lignesAEnvoyer.length === 0 || busy}
              onClick={() => void handleDeclarer()}
            >
              <Save size={14} /> Enregistrer{lignesAEnvoyer.length > 0 ? ` (${lignesAEnvoyer.length})` : ''}
            </button>
          </>
        }
      />

      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        {succes && (
          <div className="ds-alert-strip ds-alert-s" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><Check size={18} /></span>
            <div className="ds-alert-txt">{succes}</div>
          </div>
        )}

        {alertes.map((a) => (
          <div key={a.matiereId} className={`ds-alert-strip ${a.severite === 'danger' ? 'ds-alert-d' : 'ds-alert-w'}`} style={{ marginBottom: 8 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt"><strong>{a.code} — {a.nom}</strong> : {a.message}</div>
          </div>
        ))}

        <div className="ds-kpi-strip">
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Produit ce jour</div>
            <div className="ds-ks-value">{formatNumber(data?.totaux.produitCeJour ?? 0)}</div>
            <div className="ds-ks-sub">{data ? fmtDate(data.date) : '—'}</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Commandes en cours</div>
            <div className="ds-ks-value">{data?.totaux.ordresOuverts ?? 0}</div>
            <div className="ds-ks-sub">OF à produire</div>
          </div>
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Lots en attente QC</div>
            <div className="ds-ks-value">{data?.totaux.lotsEnAttenteQc ?? 0}</div>
            <div className="ds-ks-sub">Pas encore au parc</div>
          </div>
        </div>

        {/* ── 1. Production pour stock (régime courant) ───────────── */}
        <div className="ds-table-panel" style={{ marginTop: 16 }}>
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">
                <Factory size={14} style={{ display: 'inline', marginRight: 6 }} />
                Production pour stock — {fmtDate(date)}
              </div>
              <div className="ds-table-sub">
                Fabrication courante destinée au parc, sans commande. Un ordre de fabrication est généré automatiquement pour la traçabilité.
              </div>
            </div>
            <div className="ds-table-actions">
              <button className="ds-btn ds-btn-g" onClick={ajouterLigneStock} disabled={busy}>
                <Plus size={14} /> Ajouter un produit
              </button>
            </div>
          </div>

          {lignesStock.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>
              Cliquez sur « Ajouter un produit » pour déclarer ce qui a été fabriqué aujourd'hui pour le stock.
            </div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Produit</th>
                  <th style={{ width: 130 }}>Quantité produite</th>
                  <th>Impact matières</th>
                  <th style={{ width: 160 }}>Observation</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {lignesStock.map((ligne) => {
                  const produit = produits.find((p) => p.id === ligne.produitId);
                  return (
                    <tr key={ligne.key}>
                      <td>
                        <select
                          className="ds-fi"
                          value={ligne.produitId}
                          onChange={(e) => majLigneStock(ligne.key, 'produitId', e.target.value)}
                          style={{ width: '100%' }}
                        >
                          <option value="">— Sélectionner un produit —</option>
                          {produits.map((p) => (
                            <option key={p.id} value={p.id}>{p.code} · {p.designation}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number" min={0} step="any" className="ds-fi" placeholder="0"
                          value={ligne.quantite}
                          onChange={(e) => majLigneStock(ligne.key, 'quantite', e.target.value)}
                          style={{ width: 110, textAlign: 'right' }}
                        />
                        {produit && <div style={{ fontSize: 10, color: 'var(--tx-3)', marginTop: 2 }}>{produit.uniteVente}</div>}
                      </td>
                      <td style={{ fontSize: 10 }}>
                        <ImpactMatieres
                          consommation={produit?.consommationUnitaire ?? []}
                          ferraillageKgParPiece={produit?.ferraillageKgParPiece ?? null}
                          quantite={parseFloat(ligne.quantite || '0')}
                        />
                      </td>
                      <td>
                        <input
                          className="ds-fi" placeholder="Incident, aléa…"
                          value={ligne.notes}
                          onChange={(e) => majLigneStock(ligne.key, 'notes', e.target.value)}
                          style={{ width: 150, fontSize: 11 }}
                        />
                      </td>
                      <td>
                        <button
                          className="ds-btn ds-btn-g ds-btn-sm"
                          onClick={() => retirerLigneStock(ligne.key)}
                          title="Retirer cette ligne"
                          style={{ color: 'var(--danger)' }}
                        >
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── 2. Production sur commande ──────────────────────────── */}
        <div className="ds-table-panel" style={{ marginTop: 16 }}>
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">
                <ClipboardCheck size={14} style={{ display: 'inline', marginRight: 6 }} />
                Production sur commande — {ordres.length} ordre{ordres.length > 1 ? 's' : ''} en cours
              </div>
              <div className="ds-table-sub">
                Commandes spécifiques fabriquées sur mesure. L'ordre se clôture dès que la quantité planifiée est atteinte.
              </div>
            </div>
          </div>

          {isLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
          ) : ordres.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>
              Aucune commande spécifique en cours à cette date.
            </div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>OF</th>
                  <th>Produit</th>
                  <th className="ds-am">Planifié</th>
                  <th className="ds-am">Déjà produit</th>
                  <th className="ds-am">Reste</th>
                  <th style={{ width: 130 }}>Produit ce jour</th>
                  <th>Impact matières</th>
                  <th style={{ width: 160 }}>Observation</th>
                </tr>
              </thead>
              <tbody>
                {ordres.map((o) => (
                  <LigneOf
                    key={o.ordreId}
                    ordre={o}
                    valeur={saisies[o.ordreId] ?? ''}
                    note={notes[o.ordreId] ?? ''}
                    onChange={(v) => setSaisies((s) => ({ ...s, [o.ordreId]: v }))}
                    onNote={(v) => setNotes((s) => ({ ...s, [o.ordreId]: v }))}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── 3. Déjà déclaré aujourd'hui ─────────────────────────── */}
        {declarations.length > 0 && (
          <div className="ds-table-panel" style={{ marginTop: 16 }}>
            <div className="ds-table-header">
              <div>
                <div className="ds-table-title">Déjà déclaré le {fmtDate(date)}</div>
                <div className="ds-table-sub">{declarations.length} lot{declarations.length > 1 ? 's' : ''} enregistré{declarations.length > 1 ? 's' : ''}</div>
              </div>
            </div>
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Lot</th><th>Produit</th><th className="ds-am">Quantité</th><th>Régime</th><th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {declarations.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 600, fontSize: 12 }}>{d.lotNumber}</td>
                    <td style={{ fontSize: 11 }}>
                      {d.produit.code}
                      <div style={{ color: 'var(--tx-3)', fontSize: 10 }}>{d.produit.designation}</div>
                    </td>
                    <td className="ds-am">{formatNumber(d.quantite)}</td>
                    <td style={{ fontSize: 11 }}>
                      {d.pourStock
                        ? <span className="ds-bd ds-bd-i">Pour stock</span>
                        : <span className="ds-bd ds-bd-p">Sur commande</span>}
                      <div style={{ color: 'var(--tx-3)', fontSize: 10, marginTop: 2 }}>{d.ordreReference}</div>
                    </td>
                    <td style={{ fontSize: 11 }}>{d.statut === 'en_production' ? 'En attente QC' : d.statut === 'valide' ? 'Validé' : 'Rejeté'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── 4. File contrôle qualité ────────────────────────────── */}
        <div className="ds-table-panel" style={{ marginTop: 16 }}>
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">
                <PackageCheck size={14} style={{ display: 'inline', marginRight: 6 }} />
                Contrôle qualité — {fileQc.length} lot{fileQc.length > 1 ? 's' : ''} en attente
              </div>
              <div className="ds-table-sub">
                Les lots validés entrent au parc et deviennent expédiables. Les lots rejetés en sont écartés.
              </div>
            </div>
            <div className="ds-table-actions" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input className="ds-fi" placeholder="Zone parc (ex. A1) *" value={zoneParc}
                onChange={(e) => setZoneParc(e.target.value)} style={{ width: 150 }} />
              <input className="ds-fi" placeholder="Emplacement (option.)" value={emplacement}
                onChange={(e) => setEmplacement(e.target.value)} style={{ width: 160 }} />
              <button
                className="ds-btn ds-btn-p"
                disabled={selection.size === 0 || !zoneParc.trim() || busy}
                onClick={() => void handleValiderQc()}
                title={!zoneParc.trim() ? 'Renseigner la zone parc' : undefined}
              >
                <PackageCheck size={14} /> Valider &amp; placer au parc{selection.size > 0 ? ` (${selection.size})` : ''}
              </button>
              <button
                className="ds-btn ds-btn-g"
                disabled={selection.size === 0 || busy}
                onClick={() => void handleRejeter()}
                style={{ color: 'var(--danger)' }}
              >
                <X size={14} /> Rejeter
              </button>
            </div>
          </div>

          {fileQc.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>
              Aucun lot en attente de contrôle qualité.
            </div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th style={{ width: 34 }}>
                    <input type="checkbox" checked={selection.size === fileQc.length && fileQc.length > 0}
                      onChange={toggleAll} aria-label="Tout sélectionner" />
                  </th>
                  <th>Lot</th><th>OF</th><th>Produit</th>
                  <th className="ds-am">Quantité</th><th>Produit le</th><th>Observation</th>
                </tr>
              </thead>
              <tbody>
                {fileQc.map((l) => (
                  <tr key={l.id} style={selection.has(l.id) ? { background: 'var(--bg-2)' } : undefined}>
                    <td>
                      <input type="checkbox" checked={selection.has(l.id)} onChange={() => toggle(l.id)}
                        aria-label={`Sélectionner ${l.lotNumber}`} />
                    </td>
                    <td style={{ fontWeight: 600, fontSize: 12 }}>{l.lotNumber}</td>
                    <td style={{ fontSize: 11 }}>{l.ordre.reference}</td>
                    <td style={{ fontSize: 11 }}>
                      {l.ordre.produit.code}
                      <div style={{ color: 'var(--tx-3)', fontSize: 10 }}>{l.ordre.produit.designation}</div>
                    </td>
                    <td className="ds-am">{formatNumber(l.quantite)}</td>
                    <td style={{ fontSize: 11 }}>{fmtDate(l.dateProduction)}</td>
                    <td style={{ fontSize: 10, color: 'var(--tx-3)' }}>{l.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

// ── Aperçu de l'impact matières, commun aux deux régimes ───────────────────

const ImpactMatieres = ({
  consommation, ferraillageKgParPiece, quantite,
}: {
  consommation: ConsommationUnitaireMp[];
  ferraillageKgParPiece: number | null;
  quantite: number;
}) => {
  const impacts = useMemo(() => {
    if (!(quantite > 0)) return [];
    return consommation.map((c) => {
      const conso = c.parUnite * quantite;
      return { ...c, conso, stockApres: c.stockActuel - conso };
    });
  }, [consommation, quantite]);

  if (!(quantite > 0)) return <span style={{ color: 'var(--tx-3)' }}>—</span>;

  const enManque = impacts.filter((i) => i.stockApres < 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {impacts.slice(0, 4).map((i) => (
        <div key={i.matiereId} style={{ color: i.stockApres < 0 ? 'var(--danger)' : 'var(--tx-3)' }}>
          {i.code} −{formatNumber(i.conso)} {i.unite}
          <span style={{ opacity: 0.7 }}> → {formatNumber(i.stockApres)}</span>
        </div>
      ))}
      {impacts.length > 4 && <div style={{ color: 'var(--tx-3)' }}>+{impacts.length - 4} autre(s)…</div>}
      {ferraillageKgParPiece ? (
        <div style={{ color: 'var(--tx-3)' }}>acier −{formatNumber(ferraillageKgParPiece * quantite)} kg</div>
      ) : null}
      {enManque.length > 0 && (
        <div style={{ color: 'var(--danger)', fontWeight: 600, marginTop: 2 }}>
          <AlertTriangle size={9} style={{ display: 'inline', marginRight: 2 }} />
          Stock insuffisant — saisie autorisée, écart à régulariser
        </div>
      )}
    </div>
  );
};

// ── Ligne de saisie d'un OF (production sur commande) ──────────────────────

const LigneOf = ({
  ordre, valeur, note, onChange, onNote,
}: {
  ordre:    ProductionJournaliereOf;
  valeur:   string;
  note:     string;
  onChange: (v: string) => void;
  onNote:   (v: string) => void;
}) => {
  const qte = parseFloat(valeur || '0');
  const depassement = qte > 0 && qte > ordre.resteAProduire;

  return (
    <tr>
      <td style={{ fontWeight: 600, fontSize: 12 }}>
        {ordre.reference}
        <div style={{ color: 'var(--tx-3)', fontSize: 10, fontWeight: 400 }}>
          {ordre.statut === 'planifie' ? 'Planifié' : 'En cours'}
          {ordre.equipe ? ` · ${ordre.equipe.nom}` : ''}
        </div>
      </td>
      <td style={{ fontSize: 11 }}>
        {ordre.produit.code}
        <div style={{ color: 'var(--tx-3)', fontSize: 10 }}>{ordre.produit.designation}</div>
      </td>
      <td className="ds-am">{formatNumber(ordre.quantitePlanifiee)}</td>
      <td className="ds-am">{formatNumber(ordre.dejaProduit)}</td>
      <td className="ds-am" style={{ fontWeight: 600 }}>{formatNumber(ordre.resteAProduire)}</td>
      <td>
        <input
          type="number" min={0} step="any" className="ds-fi" placeholder="0"
          value={valeur} onChange={(e) => onChange(e.target.value)}
          style={{ width: 110, textAlign: 'right' }}
        />
        {depassement && (
          <div style={{ fontSize: 10, color: 'var(--warning)', marginTop: 2 }}>
            Dépasse le reste ({formatNumber(ordre.resteAProduire)}) — l'OF sera clôturé
          </div>
        )}
      </td>
      <td style={{ fontSize: 10 }}>
        <ImpactMatieres
          consommation={ordre.consommationUnitaire}
          ferraillageKgParPiece={ordre.ferraillageKgParPiece}
          quantite={qte}
        />
      </td>
      <td>
        <input
          className="ds-fi" placeholder="Incident, aléa…"
          value={note} onChange={(e) => onNote(e.target.value)}
          style={{ width: 150, fontSize: 11 }}
        />
      </td>
    </tr>
  );
};
